/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { Callout, Tldr, FaqBlock, InlineCTA } from "./shared";

export const AiSeoForSoloFoundersBody = ({ post, affiliateUrl, onTrack }) => (
  <>
    <p className="lead text-lg leading-relaxed text-white/80">
      You're building a company by yourself. You don't have a marketing team, an SEO agency
      retainer, or four hours a week to write blog posts. But your competitors have all three
      — and they're stealing your search traffic. <strong>AI SEO software for solo founders</strong>{" "}
      exists to flip that equation. Here's what to look for, what to avoid, and how to set it
      up in under an hour.
    </p>

    <Tldr>{post.tldr}</Tldr>

    <h2 id="why-different">Why solo founders need different SEO software</h2>
    <p>
      The SEO tool market was built for marketing teams. Most products assume you have a
      writer, a strategist, an editor, and a CMS publisher — and that you'll meet weekly to
      coordinate. Solo founders don't.
    </p>
    <p>Your constraints look like this:</p>
    <ul>
      <li><strong>Time:</strong> 30 minutes a week, max — and that's optimistic.</li>
      <li><strong>Budget:</strong> $50–$200/month, not $3,000/month for an agency.</li>
      <li><strong>No team:</strong> the tool has to research, write, schema and publish without a human in the loop.</li>
      <li><strong>Niche knowledge:</strong> it has to learn your tone and audience without hiring a brand consultant.</li>
      <li><strong>Brand risk:</strong> generic AI slop hurts more when you're the entire company.</li>
    </ul>
    <p>
      That's why most "AI SEO tools" don't work for solo founders. They're assistants — they
      help a human go faster. You don't have a human. You need <em>autopilot</em>.
    </p>

    <h2 id="criteria">The 5 non-negotiable criteria</h2>
    <p>Score every tool you consider against these five questions. If it can't tick all five, it's not for solo founders.</p>
    <div className="my-6 overflow-x-auto rounded-2xl border border-white/10">
      <table className="w-full min-w-[640px] text-sm">
        <thead className="bg-white/[0.03] text-left text-xs uppercase tracking-wider text-white/45">
          <tr>
            <th className="p-3">Criteria</th>
            <th className="p-3">What "good" looks like</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {[
            ["End-to-end autopilot", "Research → write → schema → publish — no human required between steps."],
            ["Brand-voice learning", "Pastes a few of your existing pages, ships content that sounds like you."],
            ["Built for AI search (AEO/GEO)", "Auto-injects FAQPage and HowTo schema. Writes question-style headings. Optimizes for ChatGPT + Perplexity + AI Overviews."],
            ["Direct CMS publishing", "Native integration with WordPress, Shopify, Webflow, Ghost, Wix or Notion. Or at minimum a webhook."],
            ["Solo-founder pricing", "Under $200/month for one-person teams. No per-seat shenanigans. Cancel anytime."],
          ].map((r) => (
            <tr key={r[0]}>
              {r.map((c, i) => (
                <td key={i} className={`p-3 ${i === 0 ? "font-display font-bold text-white" : "text-white/80"}`}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <Callout kind="info">
      <strong>Reality check:</strong> Of the 7 mainstream AI SEO tools we tested, only one
      checked all five boxes for solo founders out of the box. Most are still optimized for
      marketing teams.
    </Callout>

    <h2 id="top-pick">The top pick: Soro</h2>
    <p>
      <a href={affiliateUrl} target="_blank" rel="sponsored noopener noreferrer" onClick={() => onTrack("inline-mention", post.slug)}>Soro</a>{" "}
      was built for exactly this problem. It's the only mainstream tool we've tested that
      closes the entire loop: keyword research → article brief → AI writing in your brand voice
      → schema injection → direct publishing → rank tracking. No babysitting.
    </p>

    <h3>What Soro does well for solo founders</h3>
    <ul>
      <li><strong>Setup in 60 seconds.</strong> Paste your URL, Soro crawls your site, learns your tone, ships its first article that night.</li>
      <li><strong>Daily auto-publish.</strong> Articles ship to WordPress, Shopify, Webflow, Wix, Ghost, Notion, HubSpot or any CMS via webhook — without you opening the dashboard.</li>
      <li><strong>Schema baked in.</strong> Every article includes FAQ + HowTo + Article JSON-LD. ChatGPT and Perplexity start citing within weeks.</li>
      <li><strong>50+ languages.</strong> If you sell internationally, no separate tool needed.</li>
      <li><strong>One-person pricing.</strong> No seat-based gotchas.</li>
    </ul>

    <h3>Where Soro is opinionated (and that's the point)</h3>
    <p>
      You don't get a sandbox to fiddle in. You don't get 47 dashboards. The tool decides what
      to publish, when, and what schema to inject. If you're a solo founder who wants their
      hands off the wheel, this is the entire pitch. If you're a hands-on marketing manager
      who wants to A/B test headlines all day, look elsewhere.
    </p>

    <InlineCTA
      post={post}
      location="midpost"
      affiliateUrl={affiliateUrl}
      onTrack={onTrack}
      label={{
        title: "Want hands-off SEO that actually ranks on Google + ChatGPT?",
        sub: "Soro researches, writes, schemas and publishes daily. Set it up in 60 seconds.",
        cta: "Try Soro free",
      }}
    />

    <h2 id="alternatives">3 alternatives — and when to pick them</h2>

    <h3>Frase — if you want to write yourself</h3>
    <p>
      <strong>$14.99/month</strong>. Frase is an AI writing assistant, not autopilot. You sit
      in front of the editor, generate outlines from Google's "People also ask," and write the
      article. FAQ schema is auto-generated. HowTo and Article are not. Pick it if you
      genuinely enjoy writing and want a research speed-up. Skip it if you want hands-off.
    </p>

    <h3>NeuronWriter — if you're budget-constrained</h3>
    <p>
      <strong>$19/month</strong>. Same vibe as Frase, slightly cheaper, fewer integrations,
      strong NLP term coverage. Good fit for solo founders who want an assistant and have time
      to write. No autopilot.
    </p>

    <h3>SEO.ai — if you publish in multiple languages</h3>
    <p>
      <strong>$49/month</strong>. Best multilingual translation quality of the assistants.
      Schema injection is weak — you'll need to add FAQ/HowTo manually. Pick it only if 50%+
      of your content is non-English.
    </p>

    <Callout kind="dont">
      <strong>Skip these as a solo founder:</strong> Surfer SEO, MarketMuse and Clearscope are
      excellent — but they're <em>scorers</em>, not writers. You'd still need to write every
      article yourself. They're built for marketing teams.
    </Callout>

    <h2 id="setup">Your 30-minutes-a-week solo founder playbook</h2>
    <p>If you've picked a true autopilot tool (Soro), here's the realistic time budget:</p>

    <h3>Week 1 — Setup (45 minutes, one time)</h3>
    <ol>
      <li>Sign up. Paste your website URL.</li>
      <li>Approve the first 3 article briefs the tool generates.</li>
      <li>Connect your CMS (WordPress, Shopify, Webflow, etc.).</li>
      <li>Confirm robots.txt allows GPTBot, Google-Extended, PerplexityBot, OAI-SearchBot, CCBot.</li>
      <li>Submit sitemap to <a href="https://www.bing.com/webmasters" target="_blank" rel="noopener noreferrer">Bing Webmaster Tools</a> (non-negotiable for ChatGPT citations).</li>
    </ol>

    <h3>Every week — 30 minutes total</h3>
    <ol>
      <li><strong>10 min:</strong> Review the past week's articles in your CMS. Edit anything that doesn't sound like you.</li>
      <li><strong>10 min:</strong> Approve next week's article briefs. Add 1–2 topics from customer questions you got that week.</li>
      <li><strong>5 min:</strong> Check Search Console + Bing Webmaster impressions. Note which pages are trending.</li>
      <li><strong>5 min:</strong> Run 3 category prompts in ChatGPT Search. Log who's being cited.</li>
    </ol>

    <h3>Every month — 30 minutes</h3>
    <ol>
      <li>Identify top 2 ranking pages. Open them. Add internal links to your newer posts.</li>
      <li>Update your <code>/llms.txt</code> with any new cornerstone pages.</li>
      <li>Pick one win and post it on LinkedIn / X — builds branded search demand.</li>
    </ol>

    <h2 id="roi">The ROI math — when does this pay off?</h2>
    <p>Conservative estimates from real solo-founder customers we've spoken to:</p>
    <ul>
      <li><strong>Month 1:</strong> 4–12 articles published. First impressions in Search Console. No clicks yet.</li>
      <li><strong>Month 2:</strong> 4–8 articles indexed by Bing. First ChatGPT citations on long-tail queries. 50–200 organic visits/month.</li>
      <li><strong>Month 3:</strong> Topic cluster forming. Branded searches double. 200–800 organic visits/month.</li>
      <li><strong>Month 6:</strong> Compounding. Most solo-founder customers cross 2,000+ monthly organic visits.</li>
      <li><strong>Month 12:</strong> One-person SaaS we know hit 18,000 monthly organic visits with zero ad spend — all via Soro autopilot.</li>
    </ul>
    <p>
      At ~$100/month, you're paying $1,200/year for what would cost $36,000+/year as an agency
      retainer. Even at slow ramp, the math works after month 3.
    </p>

    <h2 id="mistakes">3 solo-founder mistakes that kill the ROI</h2>
    <ol>
      <li><strong>Treating it like a manual tool.</strong> Don't open the dashboard daily. The whole point is autopilot — touch it 30 min/week, no more.</li>
      <li><strong>Picking generic topics.</strong> Feed it the questions your <em>actual customers</em> ask in DMs and support emails. Don't optimize for traffic you can't convert.</li>
      <li><strong>Skipping Bing.</strong> Google + Bing Webmaster Tools both, day one. Bing is ~85% of ChatGPT's citation source.</li>
    </ol>

    <FaqBlock faq={post.faq} />

    <h2 id="next">Your next step</h2>
    <p>
      If you're a solo founder, the choice is simple:{" "}
      <a href={affiliateUrl} target="_blank" rel="sponsored noopener noreferrer" onClick={() => onTrack("inline-mention", post.slug)}>try Soro free</a>{" "}
      and see your first article ship tonight. Setup is 60 seconds. Cancel anytime. No
      contracts.
    </p>
    <p>
      Want the bigger picture first? Read{" "}
      <a href="/blog/best-ai-seo-tools-2026">our honest comparison of 7 AI SEO tools</a>{" "}
      or learn{" "}
      <a href="/blog/how-to-rank-on-ai-search-engines">how to rank on AI search engines</a>{" "}
      end-to-end.
    </p>
  </>
);
